'use client';

/**
 * SubPageTabs — Phase 6.5 IA primitive.
 *
 * Horizontal tab bar that sits at the top of every section page that
 * has sub-pages. The Nav v2 sidebar is intentionally limited to 2
 * levels (domain → item); deeper navigation is rendered IN-PAGE via
 * this primitive (Linear / Notion / macOS pattern, ratified with Paul
 * 2026-05-27).
 *
 * Example:
 *   /hr               → renders SubPageTabs with [Overview, Onboarding,
 *                       Performance, Training] tabs; Overview is active
 *   /hr/onboarding    → same tabs; Onboarding is active
 *
 * Anatomy:
 *   ┌─────────────────────────────────────────────────────┐
 *   │ Overview  Onboarding  Performance  Training         │
 *   └─────────────────────────────────────────────────────┘
 *
 * - One row of horizontally-scrollable tabs (overflow-x: auto, no
 *   horizontal scrollbar on hover-capable devices). When the row
 *   overflows, soft fade edges appear on the clipped side(s) so off-screen tabs stay discoverable (roster UX
 *   audit 2026-06-10 P0-3 — previously tabs clipped with no affordance).
 * - Active tab = current pathname matches the tab's `href` OR one of
 *   its `activeFor` prefixes (longest-prefix winner across all tabs'
 *   candidates — same pattern as Nav v2 sidebar). `activeFor` lets a
 *   grouped tab claim deep links that live under a different path
 *   (e.g. roster "Approvals" → /roster/timesheets also owns
 *   /roster/recalls).
 * - Each tab is a Link → uses framework router (Next.js Link via the
 *   `linkRender` prop so this stays framework-agnostic).
 * - Optional `counts` map renders a small badge next to the tab label
 *   (for example a numeric pill beside Pending or Open).
 * - Keyboard: Tab through; arrow keys NOT bound (links use Tab order).
 */

import React from 'react';
import { Popover } from '../popover';
import { CountBadge } from '../count-badge';
import { cn } from '../../lib/utils';

/**
 * Resolve which tab should be marked active for a given current
 * pathname. Longest-prefix-winner across each tab's `href` plus its
 * optional `activeFor` prefixes (matches the Nav v2 sidebar's
 * active-state rule). When two tabs both claim a path, the tab whose
 * matching candidate is LONGEST wins — e.g. `/roster/proposals/conflicts`
 * in a "Data health" tab's activeFor beats `/roster/proposals` in
 * "Plan"'s. Exported as a pure function for unit testing.
 */
export function resolveActiveTab(
  tabs: ReadonlyArray<SubPageTab>,
  currentPath: string,
): string | null {
  let best: { href: string; length: number } | null = null;
  for (const tab of tabs) {
    const candidates: readonly string[] = tab.activeFor ? [tab.href, ...tab.activeFor] : [tab.href];
    for (const candidate of candidates) {
      if (currentPath === candidate || currentPath.startsWith(`${candidate}/`)) {
        if (!best || candidate.length > best.length) {
          best = { href: tab.href, length: candidate.length };
        }
      }
    }
  }
  return best?.href ?? null;
}

export interface SubPageTab {
  /** Stable id used as the React key. */
  id: string;
  /** Display label for the tab. */
  label: string;
  /** Pathname this tab represents (for active-state + navigation). */
  href: string;
  /**
   * Optional count badge rendered next to the label
   * (for example a numeric pill beside Open). Hidden when undefined / null / 0.
   */
  count?: number | null;
  countLabel?: string;
  /**
   * Additional path prefixes that should highlight this tab (for
   * grouped IAs where deep links live outside the tab's own href
   * subtree). Active-state only — never rendered, never navigated to.
   */
  activeFor?: readonly string[];
}

export interface SubPageTabsProps {
  /** Record destinations lead merged shell navigation and cap at six on desktop. */
  record?: boolean;
  /** Maximum desktop destinations before More; mobile retains every tab. */
  primaryLimit?: number;
  /** Tab definitions in display order. */
  tabs: ReadonlyArray<SubPageTab>;
  /** Current pathname for active-state (longest-prefix-winner). */
  currentPath: string;
  /**
   * Render-prop for the navigation link. Defaults to a plain anchor.
   * Web app should wire to a Next.js `<Link>` to keep prefetch +
   * soft-navigation behaviour.
   */
  linkRender?: (props: {
    href: string;
    className: string;
    'aria-current': 'page' | undefined;
    children: React.ReactNode;
  }) => React.ReactNode;
  className?: string;
  navigationLabel?: string;
  navigationRole?: 'tablist';
  overflowLabel?: string;
}

type TabContribution = { id: string; props: SubPageTabsProps };
type SectionTabsContextValue = {
  register: (id: string, props: SubPageTabsProps) => void;
  unregister: (id: string) => void;
  registerSlot: (id: string) => () => void;
  slotId?: string;
  navigation: React.ReactNode;
};
const SectionTabsContext = React.createContext<SectionTabsContextValue | null>(null);

/** Shell and page destinations share one bar without changing their link renderers or gates. */
export function SectionTabsProvider({ children }: { children: React.ReactNode }) {
  const [contributions, setContributions] = React.useState<TabContribution[]>([]);
  const [slots, setSlots] = React.useState<string[]>([]);
  const register = React.useCallback((id: string, props: SubPageTabsProps) => {
    setContributions((previous) =>
      previous.some((item) => item.id === id)
        ? previous.map((item) => (item.id === id ? { id, props } : item))
        : [...previous, { id, props }],
    );
  }, []);
  const unregister = React.useCallback((id: string) => {
    setContributions((previous) => previous.filter((item) => item.id !== id));
  }, []);
  const registerSlot = React.useCallback((id: string) => {
    setSlots((previous) => [...previous, id]);
    return () => setSlots((previous) => previous.filter((item) => item !== id));
  }, []);
  // A page's renderer takes precedence for duplicate destinations, retaining its query parameters.
  const destinations = new Map<
    string,
    { tab: SubPageTab; render?: SubPageTabsProps['linkRender'] }
  >();
  for (const contribution of contributions) {
    for (const tab of contribution.props.tabs) {
      const previous = destinations.get(tab.href)?.tab;
      destinations.set(tab.href, {
        tab: {
          ...tab,
          id: `${contribution.id}:${tab.id}`,
          activeFor: [...new Set([...(previous?.activeFor ?? []), ...(tab.activeFor ?? [])])],
        },
        render: contribution.props.linkRender,
      });
    }
  }
  const record = contributions.find((item) => item.props.record)?.props;
  // Reorder destinations only; later page contributions still win duplicate renderers.
  const ordered = record
    ? [
        ...record.tabs.map((tab) => destinations.get(tab.href)!),
        ...Array.from(destinations.values()).filter(
          (item) => !record.tabs.some((tab) => tab.href === item.tab.href),
        ),
      ]
    : Array.from(destinations.values());
  const primary = contributions[0]?.props;
  const navigation =
    destinations.size > 0 ? (
      <SubPageTabsBar
        tabs={ordered.map(({ tab }) => tab)}
        record={Boolean(record)}
        primaryLimit={record?.primaryLimit ?? contributions.at(-1)?.props.primaryLimit}
        currentPath={record?.currentPath ?? contributions.at(-1)?.props.currentPath ?? ''}
        navigationLabel={
          record?.navigationLabel ??
          contributions.at(-1)?.props.navigationLabel ??
          primary?.navigationLabel
        }
        navigationRole={contributions.at(-1)?.props.navigationRole}
        overflowLabel={record?.overflowLabel ?? contributions.at(-1)?.props.overflowLabel}
        linkRender={(props) => destinations.get(props.href)?.render?.(props) ?? <a {...props} />}
      />
    ) : null;
  return (
    <SectionTabsContext.Provider
      value={{ register, unregister, registerSlot, slotId: slots[0], navigation }}
    >
      {children}
      {/* Legacy custom headers retain all destinations after content until they adopt the header slot. */}
      {slots.length === 0 && navigation}
    </SectionTabsContext.Provider>
  );
}

/** One fixed position after header context and actions; multiple headers cannot duplicate navigation. */
export function SectionTabsSlot({ reserveSpace = false }: { reserveSpace?: boolean } = {}) {
  const context = React.useContext(SectionTabsContext);
  const id = React.useId();
  const registerSlot = context?.registerSlot;
  React.useEffect(() => registerSlot?.(id), [id, registerSlot]);
  const ownsSlot = context?.slotId === id;
  // Opt in only where the page always contributes navigation. Reserve the same
  // row before effects register destinations, including a remount while the
  // previous slot is unregistering. Only one header per page should opt in.
  // Navigation itself still belongs exclusively to the registered owner.
  return context && (ownsSlot || reserveSpace) ? (
    <div
      className={cn('w-full min-w-0', reserveSpace && 'min-h-11 sm:min-h-9')}
      data-section-tabs-slot
    >
      {ownsSlot ? context.navigation : null}
    </div>
  ) : null;
}

export function SubPageTabs(props: SubPageTabsProps) {
  const context = React.useContext(SectionTabsContext);
  const id = React.useId();
  const register = context?.register;
  const unregister = context?.unregister;
  React.useEffect(() => () => unregister?.(id), [id, unregister]);
  const {
    tabs,
    record,
    primaryLimit,
    currentPath,
    linkRender,
    className,
    navigationLabel,
    navigationRole,
    overflowLabel,
  } = props;
  React.useEffect(() => {
    register?.(id, {
      tabs,
      record,
      primaryLimit,
      currentPath,
      linkRender,
      className,
      navigationLabel,
      navigationRole,
      overflowLabel,
    });
  }, [
    register,
    id,
    tabs,
    record,
    primaryLimit,
    currentPath,
    linkRender,
    className,
    navigationLabel,
    navigationRole,
    overflowLabel,
  ]);
  return context ? null : <SubPageTabsBar {...props} />;
}

/** Shared presentation for route tabs, including the measured People overflow menu. */
export const sectionTabClassName =
  'relative inline-flex h-11 min-h-11 sm:h-9 sm:min-h-9 snap-start shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-body font-medium transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus';

export const sectionTabActiveClassName = 'border-focus text-selected-foreground';
export const sectionTabInactiveClassName =
  'border-transparent text-text-muted hover:bg-surface-soft hover:text-text-heading';

function visualGeometry(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  // offsetWidth is the layout border-box; getBoundingClientRect is the visual
  // border-box. Keeping the ratio in one coordinate system also handles
  // borders and zoom-out (<1) correctly. jsdom has no layout box, so retain a
  // neutral scale for tests that do not provide offsetWidth.
  const layoutWidth = el.offsetWidth;
  const scale = layoutWidth > 0 && rect.width > 0 ? rect.width / layoutWidth : 1;
  const left = rect.left + el.clientLeft * scale;
  const width = el.clientWidth * scale;
  return { scale, left, right: left + width };
}

function SubPageTabsBar({
  tabs,
  record = false,
  primaryLimit,
  currentPath,
  linkRender,
  className,
  navigationLabel = 'Section navigation',
  navigationRole,
  overflowLabel,
}: SubPageTabsProps) {
  const moreLabel =
    overflowLabel ??
    (navigationLabel === 'People navigation' ? 'More People pages' : 'More section pages');
  const desktopLimit = Math.max(1, primaryLimit ?? (record ? 6 : tabs.length));
  const [moreOpen, setMoreOpen] = React.useState(false);
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const measureRef = React.useRef<HTMLDivElement | null>(null);
  const [visibleCount, setVisibleCount] = React.useState(Math.min(desktopLimit, tabs.length));
  const [overflow, setOverflow] = React.useState<{ left: boolean; right: boolean }>({
    left: false,
    right: false,
  });

  const updateOverflow = React.useCallback(() => {
    if (window.innerWidth < 640) setMoreOpen(false);
    const el = scrollerRef.current;
    if (!el) return;
    const measurements = Array.from(measureRef.current?.children ?? []);
    const { scale } = visualGeometry(el);
    const widths = measurements
      .slice(0, tabs.length)
      .map((item) => item.getBoundingClientRect().width / scale);
    const moreWidth = (measurements[tabs.length]?.getBoundingClientRect().width ?? 0) / scale;
    if (widths.some(Boolean)) {
      const available =
        el.clientWidth -
        parseFloat(getComputedStyle(el).paddingLeft || '0') -
        parseFloat(getComputedStyle(el).paddingRight || '0');
      const total = widths.reduce((sum, width) => sum + width, 0);
      let count = 0;
      let used = 0;
      for (const width of widths) {
        if (
          used + width + (total > available || tabs.length > desktopLimit ? moreWidth : 0) >
          available
        )
          break;
        count++;
        used += width;
      }
      setVisibleCount(Math.min(desktopLimit, count));
    }
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft < maxScrollLeft - 1;
    setOverflow((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, [tabs, desktopLimit]);

  React.useEffect(() => {
    updateOverflow();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateOverflow, { passive: true });
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateOverflow) : null;
    resizeObserver?.observe(el);
    if (measureRef.current) {
      for (const child of Array.from(measureRef.current.children)) resizeObserver?.observe(child);
    }
    window.addEventListener('resize', updateOverflow);
    return () => {
      el.removeEventListener('scroll', updateOverflow);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateOverflow);
    };
  }, [updateOverflow, tabs]);

  React.useEffect(() => setMoreOpen(false), [currentPath, tabs]);
  const activeHref = resolveActiveTab(tabs, currentPath);
  React.useEffect(() => {
    if (!record || window.innerWidth >= 640) return;
    const strip = scrollerRef.current;
    const active = strip?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!strip || !active) return;
    const { scale, left, right } = visualGeometry(strip);
    const bounds = active.getBoundingClientRect();
    if (bounds.left < left) {
      strip.scrollLeft = Math.max(0, strip.scrollLeft + (bounds.left - left) / scale);
    } else if (bounds.right > right) {
      strip.scrollLeft += (bounds.right - right) / scale;
    }
  }, [record, activeHref]);

  if (tabs.length === 0) return null;

  // Codex Phase 6.5 pass 2 P3: tabs no longer use `sticky top-0` — the
  // dashboard shell's header is already `sticky top-0 z-30 h-14`, and
  // a tab bar with lower z-index pinned underneath. Static positioning
  // is correct here; the dashboard header already gives users a
  // constant "where am I" anchor at the page top.
  return (
    <nav
      aria-label={navigationLabel}
      role={navigationRole}
      className={cn('relative -mt-px w-full min-w-0 border-b border-border', className)}
      data-sub-page-tabs
    >
      <div
        ref={measureRef}
        aria-hidden="true"
        style={{ visibility: 'hidden' }}
        className="pointer-events-none absolute flex w-max max-w-full overflow-hidden opacity-0"
      >
        {tabs.map((tab) => (
          <span key={tab.id} className={cn(sectionTabClassName, tab === tabs[0] && 'pl-0')}>
            {tab.label}
            {typeof tab.count === 'number' && tab.count > 0 && (
              <>
                {' '}
                <CountBadge
                  count={tab.count}
                  label={tab.countLabel ?? `${tab.label} items`}
                  compact
                />
              </>
            )}
          </span>
        ))}
        <span className={sectionTabClassName}>More</span>
      </div>
      <div
        data-tab-bar
        ref={scrollerRef}
        onFocusCapture={(event) => {
          const strip = event.currentTarget;
          const target = event.target;
          if (!(target instanceof HTMLElement) || target === strip || !strip.contains(target))
            return;
          const bounds = target.getBoundingClientRect();
          const { scale, left, right } = visualGeometry(strip);
          // Keep the focused link and its outline visible without scrolling the page vertically.
          if (bounds.left < left + 4 * scale) strip.scrollLeft += (bounds.left - left) / scale - 4;
          else if (bounds.right > right - 4 * scale)
            strip.scrollLeft += (bounds.right - right) / scale + 4;
        }}
        className={cn(
          'flex w-full flex-nowrap items-center gap-0 overflow-x-auto snap-x snap-proximity',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        {tabs.map((tab, index) => {
          const isActive = activeHref === tab.href;
          const linkClass = cn(
            sectionTabClassName,
            index === 0 && 'pl-0',
            index >= visibleCount && 'sm:hidden',
            isActive ? sectionTabActiveClassName : sectionTabInactiveClassName,
          );
          const children = (
            <>
              {tab.label}
              {typeof tab.count === 'number' && tab.count > 0 && (
                <>
                  {' '}
                  <CountBadge
                    count={tab.count}
                    label={tab.countLabel ?? `${tab.label} items`}
                    compact
                  />
                </>
              )}
            </>
          );
          const ariaCurrent = isActive ? ('page' as const) : undefined;
          return (
            <React.Fragment key={tab.id}>
              {linkRender ? (
                linkRender({
                  href: tab.href,
                  className: linkClass,
                  'aria-current': ariaCurrent,
                  children,
                })
              ) : (
                <a href={tab.href} className={linkClass} aria-current={ariaCurrent}>
                  {children}
                </a>
              )}
            </React.Fragment>
          );
        })}
        {tabs.length > visibleCount && (
          <Popover
            open={moreOpen}
            onOpenChange={setMoreOpen}
            align="end"
            focusInteractiveOnOpen
            role="region"
            ariaLabel={moreLabel}
            className="hidden min-w-48 max-h-96 overflow-y-auto p-1 sm:block"
            trigger={
              <button
                type="button"
                className={cn(
                  sectionTabClassName,
                  tabs.slice(visibleCount).some((tab) => tab.href === activeHref)
                    ? sectionTabActiveClassName
                    : sectionTabInactiveClassName,
                  'hidden sm:inline-flex',
                )}
                aria-label={moreLabel}
              >
                More
              </button>
            }
          >
            <div className="flex flex-col" onClick={() => setMoreOpen(false)}>
              {tabs.slice(visibleCount).map((tab) => {
                const props = {
                  href: tab.href,
                  className: cn(
                    sectionTabClassName,
                    tab.href === activeHref
                      ? sectionTabActiveClassName
                      : sectionTabInactiveClassName,
                  ),
                  'aria-current': tab.href === activeHref ? ('page' as const) : undefined,
                  children: (
                    <>
                      {tab.label}
                      {typeof tab.count === 'number' && tab.count > 0 && (
                        <>
                          {' '}
                          <CountBadge
                            count={tab.count}
                            label={tab.countLabel ?? `${tab.label} items`}
                          />
                        </>
                      )}
                    </>
                  ),
                };
                return (
                  <React.Fragment key={tab.id}>
                    {linkRender ? (
                      (() => {
                        const rendered = linkRender(props);
                        return React.isValidElement<{ tabIndex?: number }>(rendered)
                          ? React.cloneElement(rendered, { tabIndex: 0 })
                          : rendered;
                      })()
                    ) : (
                      <a {...props} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </Popover>
        )}
      </div>

      {/* Phone tabs remain one scrollable row; fades never cover a control. */}
      {overflow.left && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-surface-canvas to-transparent sm:hidden"
        />
      )}
      {overflow.right && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-surface-canvas to-transparent sm:hidden"
        />
      )}
    </nav>
  );
}
